import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { SavedBrandContext } from "contexts/SavedBrand";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { MouseEvent, useContext, useMemo, useState } from "react";
import { SavedBrandCollection } from "schemas/dashboard";
import { HEART_COLOR } from "styles/oneOffColor";

import { useCollection } from "utils/useCollection";

import DeleteCollectionDialog from "../DeleteCollectionDialog";
import styles from "../styles";

interface Props {
  collection: SavedBrandCollection;
  setSelectedCollection: () => void;
}

const Collection = ({ collection, setSelectedCollection }: Props) => {
  const collectionId = collection.id;
  const collectionName = collection.name;
  const { brandBeingSaved } = useContext(SavedBrandContext);
  const { collections, selectedCollectionId, setSelectedCollectionId } =
    useContext(SavedBrandCollectionsContext);
  const { deleteCollection } = useCollection();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const canDelete =
    !collection.isDefaultForVa && !brandBeingSaved && collectionId > 0;

  const allCollections = useMemo(
    () => Object.values(collections),
    [collections],
  );

  const openConfirm = (e: MouseEvent) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (deleteLoading) return;
    setConfirmOpen(false);
  };

  const handleDelete = async (options: {
    removeBrands: boolean;
    moveToCollectionId: number | null;
  }) => {
    setDeleteLoading(true);
    const wasDeleted = await deleteCollection(collectionId, options);
    if (wasDeleted && selectedCollectionId === collectionId) {
      setSelectedCollectionId(null);
    }
    setDeleteLoading(false);
    if (wasDeleted) setConfirmOpen(false);
  };

  const getActionText = () => {
    if (brandBeingSaved && collectionId === 0) {
      return (
        <Grid sx={{ px: 2 }}>
          Saved{" "}
          <Box
            component="i"
            className="fa-solid fa-bookmark"
            sx={{ color: HEART_COLOR, ml: 1 }}
          />
        </Grid>
      );
    } else {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {canDelete && (
            <Tooltip title="Delete collection">
              <IconButton
                onClick={openConfirm}
                sx={{ ...styles.circlePlus, mr: 1, color: grey[600] }}
              >
                <Box
                  component="i"
                  className="fa-regular fa-trash"
                  sx={{ fontSize: 12 }}
                />
              </IconButton>
            </Tooltip>
          )}
          <IconButton sx={styles.circlePlus}>
            <Box
              sx={styles.chevron}
              component="i"
              className="fa-regular fa-arrow-right"
            />
          </IconButton>
        </Box>
      );
    }
  };

  return (
    <Grid key={`${collectionId}-${collectionName}`}>
      <Grid
        container
        alignItems="center"
        sx={styles.collection}
        justifyContent="space-between"
        onClick={setSelectedCollection}
      >
        <Box>
          {collectionName}{" "}
          {collection.isInstagramCollection && (
            <Box
              component="i"
              className="fa-brands fa-instagram"
              sx={{ color: "#E4405F", fontSize: "0.875rem" }}
            />
          )}
          <Typography variant="body2" sx={{ color: grey[600] }}>
            Unsent ({collection.unsentCount}) | Sent ({collection.sentCount})
          </Typography>
        </Box>
        {getActionText()}
      </Grid>
      {canDelete && (
        <DeleteCollectionDialog
          open={confirmOpen}
          collection={collection}
          allCollections={allCollections}
          loading={deleteLoading}
          handleClose={closeConfirm}
          handleConfirm={handleDelete}
        />
      )}
    </Grid>
  );
};

export default Collection;
